import { InfoIcon, type DeckardIcon } from '@assets/icons';
import { Tooltip } from '@components/tooltip';
import { Typography } from '@components/typography';
import { InputBase, Stack, useTheme, type InputBaseProps } from '@mui/material';

export type TextInputProps = InputBaseProps & {
  label?: string;
  labelInfo?: string;
  maintainGutters?: boolean;
  startIcon?: DeckardIcon;
  endIcon?: DeckardIcon;
  small?: boolean;
  min?: number;
  max?: number;
  noValidate?: boolean;
  step?: number;
  containerProps?: Record<string, unknown>;
  inputBaseProps?: Record<string, unknown>;
  onClickEndIcon?: () => void;
};

export const TextInput = ({
  disabled,
  error,
  label,
  labelInfo,
  name,
  startIcon: StartIcon,
  endIcon: EndIcon,
  small = false,
  sx,
  containerProps,
  onClickEndIcon,
  min,
  max,
  noValidate,
  step,
  inputProps,
  inputBaseProps,
  startAdornment,
  endAdornment,
  ...props
}: TextInputProps) => {
  const { palette, typography } = useTheme();
  return (
    <Stack width="100%" {...containerProps}>
      {label && (
        <Typography
          className="deckard-text-input-label"
          component="label"
          htmlFor={name}
          mb={1}
          variant="caption2"
          color={
            disabled
              ? palette.input.label.disabled
              : error
                ? palette.input.label.error
                : palette.input.label.default
          }
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {label}
          {labelInfo && (
            <Tooltip title={labelInfo}>
              <InfoIcon size={10} />
            </Tooltip>
          )}
        </Typography>
      )}
      <InputBase
        fullWidth
        error={Boolean(error)}
        disabled={disabled}
        id={name}
        startAdornment={startAdornment || (StartIcon && <StartIcon size={16} />)}
        endAdornment={
          endAdornment ||
          (EndIcon && (
            <EndIcon
              size={16}
              style={{ cursor: disabled ? 'default' : 'pointer' }}
              onClick={() => {
                if (onClickEndIcon) {
                  onClickEndIcon();
                }
              }}
            />
          ))
        }
        sx={{
          ...(small ? { ...typography.caption2, height: '24px', minHeight: '24px' } : {}),
          ...sx,
        }}
        inputProps={{ min, max, step, noValidate, ...inputProps }}
        {...props}
      />
    </Stack>
  );
};
